import {
  suppliesInventoryConfigIdFormat,
  suppliesInventoryReorderIdFormat,
} from "@redotech/redo-marketplace-id";
import * as z from "zod";
import { SuppliesInventoryConfigRowSchema } from "./get-supplies-inventory-configs";

const SuppliesInventoryReorderRowSchema = z.object({
  reorderId: suppliesInventoryReorderIdFormat.schema,
  // Mongo Order ObjectId (hex) of the order placed for this reorder.
  orderId: z.string(),
  orderName: z.string().nullable(),
  date: z.string(),
  quantityOrderedCases: z.number(),
  fulfillmentStatus: z.string().nullable(),
  // Null until the reorder has been received/restocked.
  receivedDate: z.string().nullable(),
});

export type SuppliesInventoryReorderRow = z.infer<
  typeof SuppliesInventoryReorderRowSchema
>;

const SuppliesInventoryChangeRowSchema = z.object({
  id: z.string(),
  date: z.string(),
  // Pretty batch id from the print job's fulfillment batch. Null when the row
  // has no print job (e.g. restocks) or the print job has only group ids.
  prettyBatchId: z.string().nullable(),
  // Signed inventory delta (negative = consumed, positive = restocked).
  delta: z.number(),
  notes: z.string().nullable(),
  referenceId: z.string().nullable(),
  userName: z.string().nullable(),
  // Deduped order names from the print job's batch / fulfillment groups.
  orderNames: z.array(z.string()),
  // Label PDF url from the print job; opens in a new tab.
  pdfUrl: z.string().nullable(),
});

export type SuppliesInventoryChangeRow = z.infer<
  typeof SuppliesInventoryChangeRowSchema
>;

export const getSuppliesInventoryConfigDetail = {
  name: "getSuppliesInventoryConfigDetail" as const,
  input: z.object({ configId: suppliesInventoryConfigIdFormat.schema }),
  output: z.object({ config: SuppliesInventoryConfigRowSchema }),
};

export const getSuppliesInventoryConfigReorders = {
  name: "getSuppliesInventoryConfigReorders" as const,
  input: z.object({
    configId: suppliesInventoryConfigIdFormat.schema,
    pageNumber: z.number(),
    pageSize: z.number(),
  }),
  output: z.object({ reorders: z.array(SuppliesInventoryReorderRowSchema) }),
};

export const getSuppliesInventoryConfigReordersCount = {
  name: "getSuppliesInventoryConfigReordersCount" as const,
  input: z.object({ configId: suppliesInventoryConfigIdFormat.schema }),
  output: z.object({ total: z.number() }),
};

export const getSuppliesInventoryConfigInventoryHistory = {
  name: "getSuppliesInventoryConfigInventoryHistory" as const,
  input: z.object({
    configId: suppliesInventoryConfigIdFormat.schema,
    pageNumber: z.number(),
    pageSize: z.number(),
  }),
  output: z.object({ changes: z.array(SuppliesInventoryChangeRowSchema) }),
};

export const getSuppliesInventoryConfigInventoryHistoryCount = {
  name: "getSuppliesInventoryConfigInventoryHistoryCount" as const,
  input: z.object({ configId: suppliesInventoryConfigIdFormat.schema }),
  output: z.object({ total: z.number() }),
};

export const markSuppliesReorderReceived = {
  name: "markSuppliesReorderReceived" as const,
  input: z.object({ reorderId: suppliesInventoryReorderIdFormat.schema }),
  output: z.object({}),
};
